/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, ReactNode } from "react";
import { useLocation } from "react-router";

import { useSegmentPage } from "sj-ui-toolkit/src/hooks";

/**
 * Learning context types
 */
export const LearnContextType = {
  TOURNAMENT: "tournament",
  ASSIGNMENT: "assignment",
  PATH: "path",
  SCORM: "scorm",
  GUEST: "guest",
} as const;

export type LearnContextTypeValue = "tournament" | "assignment" | "path" | "scorm" | "guest";

export interface LearnContextValue {
  context: LearnContextTypeValue | null;
  isTournament: boolean;
  isAssignment: boolean;
  isPath: boolean;
  isScorm: boolean;
  isGuest: boolean;
}

const LearnContext = createContext<LearnContextValue | null>(null);

interface LearnContextProviderProps {
  children: ReactNode;
  contextType?: LearnContextTypeValue | null;
}

/**
 * LearnContextProvider provides information about the current learning context
 * (tournament, assignment, path, or scorm)
 */
export function LearnContextProvider({ children, contextType = null }: LearnContextProviderProps) {
  const location = useLocation();
  useSegmentPage();

  // Determine context type from props or URL path
  const context = useMemo<LearnContextTypeValue | null>(() => {
    // If explicitly provided as prop, use that
    if (contextType) {
      return contextType;
    }

    // Otherwise, determine from the first path segment
    const firstSegment = location.pathname.split("/")[1];

    if (firstSegment === LearnContextType.TOURNAMENT) {
      return LearnContextType.TOURNAMENT;
    }
    if (firstSegment === LearnContextType.ASSIGNMENT) {
      return LearnContextType.ASSIGNMENT;
    }
    if (firstSegment === LearnContextType.PATH) {
      return LearnContextType.PATH;
    }
    if (firstSegment === LearnContextType.SCORM) {
      return LearnContextType.SCORM;
    }
    if (firstSegment === LearnContextType.GUEST) {
      return LearnContextType.GUEST;
    }

    return null;
  }, [contextType, location.pathname]);

  const value: LearnContextValue = {
    context,
    isTournament: context === LearnContextType.TOURNAMENT,
    isAssignment: context === LearnContextType.ASSIGNMENT,
    isPath: context === LearnContextType.PATH,
    isScorm: context === LearnContextType.SCORM,
    isGuest: context === LearnContextType.GUEST,
  };

  return <LearnContext.Provider value={value}>{children}</LearnContext.Provider>;
}

/**
 * Hook to access the learn context
 */
export function useLearnContext(): LearnContextValue {
  const context = useContext(LearnContext);
  if (context === null) {
    throw new Error("useLearnContext must be used within LearnContextProvider");
  }
  return context;
}
